import axios from "axios";
import { generateTraceId } from "../../../helpers";
import { config } from "../../../../config/envs";

// proxy do monolito jusfy-backend para as rotas admin do calculator-api
// (a API key de serviço fica no backend, nunca no browser)
const adminBaseUrl = config?.app?.adminBaseUrl ?? "";
const clientsUrl = `${adminBaseUrl}/calculator-admin/clients`;

const traceHeaders = () => ({ headers: { "x-trace-id": generateTraceId() } });

// GET /calculator-admin/usage/:endpoint
//   -> { period, totals, client, byClient, breakdown[] }
//
// A resposta varia em duas dimensões independentes e, em cada uma, exatamente
// uma chave existe. A dimensão de rota (byCalculator | byQueryType) é achatada
// num `breakdown` único — a tela não precisa saber qual rota chamou. A dimensão
// de cliente (client | byClient) é preservada, porque a tela renderiza coisas
// diferentes em cada caso.
const getUsage = async (
  endpoint,
  breakdownKey,
  slugKey,
  { clientId, from, to },
) => {
  // params montados um a um: qualquer chave desconhecida devolve 422, inclusive
  // por typo. `backendClientId` não é enviado — ele não alcança cliente sem
  // vínculo na Jusfy, e mandar os dois é 400 (são mutuamente exclusivos).
  const searchParams = new URLSearchParams();
  if (clientId) searchParams.append("clientId", clientId);
  if (from) searchParams.append("from", from);
  if (to) searchParams.append("to", to);

  // O 404 (CALCULATOR_API_CLIENT_NOT_FOUND, clientId de lista desatualizada)
  // vira estado vazio, igual a período sem consumo — que é 200 com zeros e
  // arrays vazios (Caso D do contrato). É RF-20: as duas causas resultam no
  // mesmo vazio, sem alerta de erro. Os outros status seguem propagando.
  let response;
  try {
    response = await axios.get(
      `${adminBaseUrl}/calculator-admin/usage/${endpoint}?${searchParams}`,
      traceHeaders(),
    );
  } catch (error) {
    if (error.response?.status === 404) return null;
    throw error;
  }

  const usage = response.data?.data;
  if (!usage) return null;

  return {
    period: usage.period,
    totals: usage.totals,
    // `?? null` nas duas para distinguir "não veio na resposta" de
    // `byClient: []`, que é consolidado legítimo sem consumo no período
    client: usage.client ?? null,
    byClient: usage.byClient ?? null,
    breakdown: (usage[breakdownKey] ?? []).map(
      ({ [slugKey]: slug, ...counters }) => ({ slug, ...counters }),
    ),
  };
};

export const getCalculationsUsage = filters =>
  getUsage("calculations", "byCalculator", "calculator", filters);

export const getQueriesUsage = filters =>
  getUsage("queries", "byQueryType", "queryType", filters);

// GET /calculator-admin/clients -> alimenta o seletor de cliente.
//
// O backend ordena por nome e não pagina: a lista vem inteira. Não traz
// `apiKey` — o proxy a remove da listagem de propósito, para o seletor não
// arrastar a credencial de todos os clientes até o browser.
//
// O `404` NÃO é absorvido aqui: isso é exclusivo de `getUsage`, onde significa
// ausência de dado. Numa listagem, absorver esconderia falha real.
export const listClients = async () => {
  const response = await axios.get(clientsUrl, traceHeaders());
  return response.data?.data ?? [];
};

// GET /calculator-admin/clients/:id -> o cliente COM a apiKey atual.
//
// É a rota de "o cliente perdeu a key": reexibe a MESMA, sem rotacionar —
// rotacionar quebraria a integração dele na hora. O 404 propaga (id inexistente
// é falha real, não ausência de dado) e sinaliza lista desatualizada.
export const getClient = async clientId => {
  const response = await axios.get(
    `${clientsUrl}/${encodeURIComponent(clientId)}`,
    traceHeaders(),
  );
  return response.data?.data ?? null;
};

// POST /calculator-admin/clients -> 201 com a apiKey gerada.
//
// `jusfyClientId` é o id do cliente na Jusfy (o mesmo `cliente_id` de
// /clientes/:cliente_id) e é serializado como `backendClientId` — nome de
// protocolo, que vaza aqui e em nenhum outro lugar do front.
//
// A chave é OMITIDA quando não informada, em vez de enviada como null: o backend
// aplica `null` por default, e ser explícito aqui é o que impede o bug quando o
// PATCH ganhar o vínculo, onde `null` DESVINCULA e ausente não mexe.
//
// `scopes` são os produtos liberados: sem eles o cliente nasce com `[]` e não
// acessa nada.
//
// Sem retry: a rota não é idempotente e o calculator-api não aceita chave de
// idempotência, então um 502 não diz se o cliente foi criado. Repetir criaria
// duplicata com uma key que ninguém usa. A ausência é deliberada.
export const createClient = async ({ name, jusfyClientId, scopes }) => {
  const response = await axios.post(
    clientsUrl,
    {
      name,
      scopes,
      ...(jusfyClientId ? { backendClientId: jusfyClientId } : {}),
    },
    traceHeaders(),
  );
  return response.data?.data ?? null;
};

// PATCH /calculator-admin/clients/:id com `{ scopes }` -> o cliente atualizado.
//
// A lista SUBSTITUI a anterior inteira (`[]` esvazia; `null` é recusado), então
// quem chama manda a lista completa, inclusive os escopos que a tela não
// conhece. Escopo desconhecido pelo calculator-api é 400 e nada é gravado.
//
// Só os escopos entram no corpo: nome, vínculo e status continuam fora desta
// tela, e mandar `backendClientId: null` aqui DESVINCULARIA o cliente. Sem
// retry, pelo mesmo motivo do createClient.
export const updateClientScopes = async (clientId, scopes) => {
  const response = await axios.patch(
    `${clientsUrl}/${encodeURIComponent(clientId)}`,
    { scopes },
    traceHeaders(),
  );
  return response.data?.data ?? null;
};

// POST /calculator-admin/clients/:id/api-key/rotate -> 200 com a key NOVA.
//
// Não há período de graça: a key anterior toma 401 na chamada seguinte e a
// integração do cliente cai até ele trocar o segredo. Só chamar atrás de
// confirmação explícita. Os escopos do cliente são preservados. Sem retry, pelo mesmo motivo do createClient.
export const rotateApiKey = async clientId => {
  const response = await axios.post(
    `${clientsUrl}/${encodeURIComponent(clientId)}/api-key/rotate`,
    undefined,
    traceHeaders(),
  );
  return response.data?.data ?? null;
};
